import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    IconBrandInstagram,
    IconBrandTelegram,
    IconBrandWhatsapp,
    IconBrandX,
    IconCheck,
    IconDownload,
    IconLink,
    IconX,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import useModalZIndex from '@hooks/useModalZIndex';
import { Button, Text } from '@fragments';
import logoFullUrl from '@assets/logo/M_WHITE_LOGO_FULL.svg';
import logoSquareUrl from '@assets/logo/M_WHITE_LOGO_SQUARE.svg';

import { SHARE_TINTS, TINT_ANGLE, findTint } from './shareTints';
import { LAYOUT, initials as initialsOf, renderShareCard, canvasToBlob } from './renderShareCard';
import { buildQr, qrPath } from './qr';

import './styles.scss';

// Side of the subject image on the card — wrappers size their own `media` node
// with it so the DOM card and the downloaded PNG agree.
export const MEDIA_SIZE = LAYOUT.subject.size;

// Built-in channel ids, in the order the default row shows them.
const DEFAULT_CHANNELS = ['download', 'copy', 'whatsapp', 'instagram', 'telegram', 'x'];

// Resolves a CSS color token to a concrete color string canvas can use.
function readColor(token) {
    const probe = document.createElement('span');
    probe.style.color = `var(${token})`;
    probe.style.position = 'absolute';
    probe.style.visibility = 'hidden';
    document.body.appendChild(probe);
    const color = getComputedStyle(probe).color;
    probe.remove();
    return color;
}

function stripProtocol(url) {
    return String(url).replace(/^https?:\/\//, '');
}

function fileSlugify(value) {
    return String(value || 'ginastee').trim().replace(/\s+/g, '-').toLowerCase();
}

/**
 * ShareCardModal — a purpose-built overlay (not the Modal fragment) made of
 * three separate parts: the flat stage sized to the 3:4 share card, a row of
 * share channels beneath it, and a bare tint palette at the right. The card can
 * be downloaded as a PNG or handed to the native share sheet.
 *
 * The chrome (channels, tints, errors) is subject-agnostic and reads from
 * `common:share.*`; everything that names what is being shared arrives as props
 * from a thin wrapper — `ShareProfileModal` for people, `ShareCommunityModal`
 * for communities, `DownloadAppModal` for the app itself.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {string}  props.url          the link the QR encodes and the channels send
 * @param {string} [props.displayUrl]  what the card prints (defaults to `url`
 *                                     without its protocol) — lets a link that
 *                                     carries tracking params show up clean
 * @param {string}  props.title        modal heading
 * @param {string}  props.subtitle     modal sub-heading
 * @param {string}  props.shareText    message the social channels carry
 * @param {string}  props.fileSlug     basename of the downloaded PNG
 * @param {{media:React.ReactNode, name:string, caption?:string, image?:string,
 *          initials?:string, shape?:'circle'|'squircle'}} props.card
 * @param {(string|{id:string, icon:React.ReactNode, label:string, onClick:Function})[]} [props.channels]
 *                                     the channel row, in order: built-in ids
 *                                     ('download' | 'copy' | 'whatsapp' |
 *                                     'instagram' | 'telegram' | 'x') and/or
 *                                     custom entries. Defaults to every built-in.
 * @param {boolean} [props.palette]    show the tint palette (default true)
 * @param {Function} props.onClose
 */
export default function ShareCardModal({ open, url, displayUrl, title, subtitle, shareText, fileSlug, card, channels, palette = true, onClose }) {
    const { t } = useTranslation('common');
    const { zIndex } = useModalZIndex(open);
    const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');

    const [tintId, setTintId] = useState('neutral');
    const [busy, setBusy] = useState(null);
    const [copied, setCopied] = useState(false);

    const tint = findTint(tintId);
    const gradId = `share-qr-grad-${uid}`;
    const titleId = `share-title-${uid}`;

    const printedUrl = useMemo(() => displayUrl || stripProtocol(url || ''), [displayUrl, url]);

    // One module matrix feeds both the on-screen SVG and the download canvas.
    const qr = useMemo(() => (url ? buildQr(url, 'M', LAYOUT.qr.excavate) : null), [url]);
    const qrD = useMemo(() => (qr ? qrPath(qr) : ''), [qr]);

    // ESC closes; the page behind stops scrolling while open.
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = previous;
        };
    }, [open, onClose]);

    useEffect(() => {
        if (!open) { setCopied(false); setBusy(null); }
    }, [open]);

    const buildImage = useCallback(async () => {
        const canvas = await renderShareCard({
            card,
            url,
            displayUrl: printedUrl,
            tint,
            qr,
            logoFullUrl,
            logoSquareUrl,
            colors: {
                surface: readColor('--surface-10'),
                ink: readColor('--surface-1'),
                sub: readColor('--surface-4'),
                soft: readColor('--surface-5'),
                well: readColor('--surface-9'),
            },
        });
        return canvasToBlob(canvas);
    }, [card, url, printedUrl, tint, qr]);

    const fileName = `ginastee-${fileSlugify(fileSlug || card?.name)}.png`;

    const saveBlob = useCallback((blob) => {
        const href = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = href;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(href), 1000);
    }, [fileName]);

    async function handleDownload() {
        setBusy('download');
        try {
            saveBlob(await buildImage());
        } catch (e) {
            window.setSnackbar?.(t('share.imageError'));
        } finally {
            setBusy(null);
        }
    }

    async function handleCopy() {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            window.setSnackbar?.(t('share.linkCopied'));
            setTimeout(() => setCopied(false), 1800);
        } catch (e) {
            window.setSnackbar?.(t('share.copyError'));
        }
    }

    function openWindow(href) {
        window.open(href, '_blank', 'noopener,noreferrer');
    }

    // Instagram has no web share URL: hand the card to the native share sheet
    // where available (the user picks Instagram there), else save the image.
    async function handleInstagram() {
        setBusy('instagram');
        try {
            const blob = await buildImage();
            const file = new File([blob], fileName, { type: 'image/png' });
            if (navigator.canShare?.({ files: [file] })) {
                await navigator.share({ files: [file], title: shareText, text: `${shareText} ${url}` });
            } else {
                saveBlob(blob);
                window.setSnackbar?.(t('share.instagramHint'));
            }
        } catch (e) {
            if (e?.name !== 'AbortError') window.setSnackbar?.(t('share.imageError'));
        } finally {
            setBusy(null);
        }
    }

    const builtinChannels = {
        download: { id: 'download', icon: <IconDownload />, label: t('share.download'), onClick: handleDownload },
        copy: { id: 'copy', icon: copied ? <IconCheck /> : <IconLink />, label: copied ? t('share.linkCopied') : t('share.copyLink'), onClick: handleCopy },
        whatsapp: { id: 'whatsapp', icon: <IconBrandWhatsapp />, label: t('share.whatsapp'), onClick: () => openWindow(`https://wa.me/?text=${encodeURIComponent(`${shareText} ${url}`)}`) },
        instagram: { id: 'instagram', icon: <IconBrandInstagram />, label: t('share.instagram'), onClick: handleInstagram },
        telegram: { id: 'telegram', icon: <IconBrandTelegram />, label: t('share.telegram'), onClick: () => openWindow(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareText)}`) },
        x: { id: 'x', icon: <IconBrandX />, label: t('share.x'), onClick: () => openWindow(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`) },
    };

    // A wrapper picks its row by built-in id, or hands over channels of its own.
    const channelList = (channels || DEFAULT_CHANNELS)
        .map(ch => (typeof ch === 'string' ? builtinChannels[ch] : ch))
        .filter(Boolean);

    if (!open || !card || !qr) return null;

    const showPalette = palette !== false;

    const tintStyle = tint.stops
        ? { '--tint-a': tint.stops[0], '--tint-b': tint.stops[1], '--tint-angle': `${TINT_ANGLE}deg` }
        : undefined;

    return createPortal(
        <div className="share-sheet" style={{ zIndex }} role="dialog" aria-modal="true" aria-labelledby={titleId}>
            {/* `MuiBackdrop-root` picks up the app's themed backdrop color, the
                same one the Modal fragment's MUI backdrop wears. */}
            <div className="share-sheet__backdrop MuiBackdrop-root" onClick={onClose} aria-hidden="true" />

            <div className={`share-sheet__layout${showPalette ? '' : ' share-sheet__layout--no-palette'}`} style={tintStyle}>
                {/* ── Stage: translucent surface sized to the card ───────── */}
                <section className={`share-sheet__stage${tint.stops ? ' share-sheet__stage--tinted' : ''}`}>
                    <header className="share-sheet__head">
                        <div className="share-sheet__heading">
                            <Text.h4 weight={500} contrast={10} id={titleId}>{title}</Text.h4>
                            <Text.span contrast={6}>{subtitle}</Text.span>
                        </div>
                        <Button
                            variant="text"
                            color="inverse"
                            size="small"
                            icon={<IconX />}
                            onClick={onClose}
                            aria-label={t('actions.close')}
                            tooltip={t('actions.close')}
                            preventLoading
                        />
                    </header>

                    {/* The palette hangs off the card's right edge, vertically
                        centered on it, outside the stage surface. */}
                    <div className="share-sheet__card-slot">
                    <div className={`share-card${tint.stops ? ' share-card--tinted' : ''}`}>
                        <span
                            className="share-card__logo"
                            style={{ WebkitMaskImage: `url(${logoFullUrl})`, maskImage: `url(${logoFullUrl})` }}
                            aria-hidden="true"
                        />

                        <div className="share-card__identity">
                            {card.media || <ShareCardMedia card={card} />}
                            <Text.h4 weight={500} contrast={10} maxLines={1}>{card.name}</Text.h4>
                            {card.caption && <Text.caption contrast={6}>{card.caption}</Text.caption>}
                        </div>

                        <div className="share-card__qr" style={{ width: LAYOUT.qr.size, height: LAYOUT.qr.size }}>
                            <svg
                                viewBox={`0 0 ${qr.size} ${qr.size}`}
                                width={LAYOUT.qr.size}
                                height={LAYOUT.qr.size}
                                role="img"
                                aria-label={printedUrl}
                            >
                                {tint.stops && (
                                    <defs>
                                        <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
                                            <stop offset="0" stopColor={tint.stops[0]} />
                                            <stop offset="1" stopColor={tint.stops[1]} />
                                        </linearGradient>
                                    </defs>
                                )}
                                <path d={qrD} fill={tint.stops ? `url(#${gradId})` : 'currentColor'} fillRule="evenodd" />
                            </svg>
                            <span
                                className="share-card__qr-mark"
                                style={{
                                    width: LAYOUT.qr.mark,
                                    height: LAYOUT.qr.mark,
                                    WebkitMaskImage: `url(${logoSquareUrl})`,
                                    maskImage: `url(${logoSquareUrl})`,
                                }}
                                aria-hidden="true"
                            />
                        </div>

                        <Text.caption contrast={7} maxLines={1} className="share-card__link">{printedUrl}</Text.caption>
                    </div>

                    {showPalette && (
                        <aside className="share-sheet__palette" aria-label={t('share.colors')} role="radiogroup">
                            {SHARE_TINTS.map(option => (
                                <button
                                    key={option.id}
                                    type="button"
                                    role="radio"
                                    aria-checked={option.id === tintId}
                                    aria-label={t(`share.tints.${option.id}`)}
                                    title={t(`share.tints.${option.id}`)}
                                    className={`share-swatch${option.id === tintId ? ' share-swatch--active' : ''}${option.stops ? '' : ' share-swatch--neutral'}`}
                                    style={option.stops ? { '--swatch-a': option.stops[0], '--swatch-b': option.stops[1] } : undefined}
                                    onClick={() => setTintId(option.id)}
                                />
                            ))}
                        </aside>
                    )}
                    </div>
                </section>

                {/* ── Channels: separate, fit-width surface under the stage ─ */}
                <section className="share-sheet__channels" aria-label={t('share.channels')}>
                    {channelList.map(ch => (
                        <button
                            key={ch.id}
                            type="button"
                            className={`share-channel${busy === ch.id ? ' share-channel--busy' : ''}`}
                            onClick={ch.onClick}
                            disabled={!!busy}
                        >
                            <span className="share-channel__well">{ch.icon}</span>
                            <Text.caption contrast={7}>{ch.label}</Text.caption>
                        </button>
                    ))}
                </section>

            </div>
        </div>,
        document.body,
    );
}

/**
 * Default subject image: the card's own square well, in the shape the canvas
 * draws. Wrappers that already own an avatar component (people) pass `media`
 * instead and this never renders.
 */
function ShareCardMedia({ card }) {
    const shape = card.shape === 'squircle' ? 'squircle' : 'circle';

    return (
        <span
            className={`share-card__media share-card__media--${shape}`}
            style={{ width: MEDIA_SIZE, height: MEDIA_SIZE }}
        >
            {card.image
                ? <img src={card.image} alt="" />
                : <Text.h4 weight={600} contrast={7}>{card.initials || initialsOf(card.name)}</Text.h4>}
        </span>
    );
}
